import React from "react";
import {
  Modal,
  ModalBody,
  Input,
  Label,
  FormFeedback,
  Button,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import "./CreateTenantUserModal.css";

interface CreateTenantUserModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (values: CreateTenantUserFormValues) => void;
  isLoading?: boolean;
  error?: string | null;
  tenantId: string;
}

export interface CreateTenantUserFormValues {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  tenantId: string;
}

// Map role names to role IDs from the database (tenant roles only)
const TENANT_ROLE_ID_MAP: Record<string, string> = {
  TENANT_ADMIN: "550e8400-e29b-41d4-a716-446655440003",
  TENANT_USER: "550e8400-e29b-41d4-a716-446655440004",
};

const CreateTenantUserModal: React.FC<CreateTenantUserModalProps> = ({
  show,
  onClose,
  onSubmit,
  isLoading = false,
  error = null,
  tenantId,
}) => {
  const { t } = useTranslation();
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      role: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required(
        t("tenants.createUserModal.firstNameRequired")
      ),
      lastName: Yup.string().required(
        t("tenants.createUserModal.lastNameRequired")
      ),
      email: Yup.string()
        .email(t("tenants.createUserModal.invalidEmail"))
        .required(t("tenants.createUserModal.emailRequired")),
      role: Yup.string().required(t("tenants.createUserModal.roleRequired")),
    }),
    onSubmit: (values) => {
      // Transform the data to match backend expectations
      const userData = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        roleId: TENANT_ROLE_ID_MAP[values.role], // Map role name to role ID
        tenantId: tenantId,
      };
      onSubmit(userData as any);
    },
  });

  const handleClose = () => {
    validation.resetForm();
    onClose();
  };

  return (
    <Modal
      isOpen={show}
      toggle={handleClose}
      centered
      className="create-tenant-user-modal"
    >
      <ModalBody className="create-tenant-user-modal-body">
        <div className="create-tenant-user-container">
          {/* Header */}
          <div className="create-tenant-user-header">
            <h3 className="create-tenant-user-title">
              {t("tenants.createUserModal.title")}
            </h3>
            <p className="create-tenant-user-subtitle">
              {t("tenants.createUserModal.subtitle")}
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="error-alert">
              <i className="ri-error-warning-line"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form
            onSubmit={validation.handleSubmit}
            className="create-tenant-user-form"
          >
            {/* First Name Field */}
            <div className="form-field">
              <Label className="form-label">
                {t("common.labels.firstName")}
                <span className="required-asterisk">*</span>
              </Label>
              <Input
                type="text"
                name="firstName"
                className="form-input"
                placeholder=" "
                value={validation.values.firstName}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.firstName && validation.errors.firstName
                    ? true
                    : false
                }
              />
              {validation.touched.firstName && validation.errors.firstName && (
                <FormFeedback type="invalid">
                  {validation.errors.firstName}
                </FormFeedback>
              )}
            </div>

            {/* Last Name Field */}
            <div className="form-field">
              <Label className="form-label">
                {t("common.labels.lastName")}
                <span className="required-asterisk">*</span>
              </Label>
              <Input
                type="text"
                name="lastName"
                className="form-input"
                placeholder=" "
                value={validation.values.lastName}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.lastName && validation.errors.lastName
                    ? true
                    : false
                }
              />
              {validation.touched.lastName && validation.errors.lastName && (
                <FormFeedback type="invalid">
                  {validation.errors.lastName}
                </FormFeedback>
              )}
            </div>

            {/* Email Field */}
            <div className="form-field">
              <Label className="form-label">
                {t("common.labels.email")}
                <span className="required-asterisk">*</span>
              </Label>
              <Input
                type="email"
                name="email"
                className="form-input"
                placeholder=" "
                value={validation.values.email}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.email && validation.errors.email
                    ? true
                    : false
                }
              />
              {validation.touched.email && validation.errors.email && (
                <FormFeedback type="invalid">
                  {validation.errors.email}
                </FormFeedback>
              )}
            </div>

            {/* Role Field - Only Tenant Roles */}
            <div className="form-field">
              <Label className="form-label">
                {t("common.labels.role")}
                <span className="required-asterisk">*</span>
              </Label>
              <Input
                type="select"
                name="role"
                className="form-select"
                value={validation.values.role}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.role && validation.errors.role
                    ? true
                    : false
                }
              >
                <option value="">
                  {t("tenants.createUserModal.selectRole")}
                </option>
                <option value="TENANT_ADMIN">
                  {t("common.roles.TENANT_ADMIN")}
                </option>
                <option value="TENANT_USER">
                  {t("common.roles.TENANT_USER")}
                </option>
              </Input>
              {validation.touched.role && validation.errors.role && (
                <FormFeedback type="invalid">
                  {validation.errors.role}
                </FormFeedback>
              )}
            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <Button
                type="submit"
                color="primary"
                className="btn-send-invitation"
                disabled={isLoading}
              >
                {isLoading
                  ? t("tenants.createUserModal.creating")
                  : t("tenants.createUserModal.submit")}
              </Button>
              <Button
                type="button"
                className="btn-cancel"
                onClick={handleClose}
                disabled={isLoading}
              >
                {t("common.actions.cancel")}
              </Button>
            </div>
          </form>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default CreateTenantUserModal;
